import { defineStore } from "pinia";
import type { SubscriptionState } from "@/types/subscription";

const LOCAL_STORAGE_SUBSCRIPTION_STATE_KEY = "subscription.state";

export const useSubscriptionStore = defineStore("subscription", () => {
  const wizardState = useLocalStorage<SubscriptionState | null>(
    LOCAL_STORAGE_SUBSCRIPTION_STATE_KEY,
    null,
    {
      serializer: {
        read: (value: string) =>
          $ck.utils.parseObfuscatedParams<SubscriptionState>(value) ?? null,
        write: (value: SubscriptionState | null) =>
          !value ? "" : $ck.utils.obfuscateParams(value),
      },
    },
  );
  const paymentCheckoutSession = ref<CheckoutSessionDto>();
  const paymentMethodUpdateCheckoutSession = ref<CheckoutSessionDto>();

  const isUpgradeConfirmationShown = ref(false);

  const getAvailablePlans = useGetAvailablePlans();

  const planList = computed(() => {
    return getAvailablePlans.data.value || [];
  });

  const isWizardInitialized = computed(() => {
    return !!wizardState.value;
  });

  const targetSubscription = computed(() =>
    wizardState.value
      ? {
          plan: wizardState.value.plan,
          priceId: wizardState.value.priceId,
        }
      : undefined,
  );

  const sourceSubscription = computed(() => {
    if (
      !wizardState.value ||
      !wizardState.value.sourceSlug ||
      !wizardState.value.sourcePriceId
    ) {
      return;
    }

    return {
      plan: wizardState.value.sourceSlug,
      priceId: wizardState.value.sourcePriceId,
    };
  });

  const latestVisitedStepIndex = computed({
    get: () => wizardState.value?.latestVisitedStepIndex ?? 0,
    set: (value: number) => {
      if (!wizardState.value) return;
      wizardState.value.latestVisitedStepIndex = value;
    },
  });

  const unsavedChangesState = reactive({
    hasUnsavedChanges: false,
    showLeaveConfirmation: false,
    confirmationLeaveRoute: undefined as string | undefined,
  });

  const loadAvailablePlans = async () => {
    if (getAvailablePlans.data.value?.length) {
      return getAvailablePlans.data.value;
    }
    return getAvailablePlans.safeFetch();
  };

  const confirmUnsavedChangesBeforeLeaving = (toRoute: string) => {
    if (!unsavedChangesState.hasUnsavedChanges) return false;

    unsavedChangesState.showLeaveConfirmation = true;
    unsavedChangesState.confirmationLeaveRoute = toRoute;
    return true;
  };

  function init({
    plan,
    priceId,
    sourceSlug,
    sourcePriceId,
  }: Omit<SubscriptionState, "latestVisitedStepIndex">) {
    wizardState.value = {
      plan,
      priceId,
      sourceSlug,
      sourcePriceId,
      latestVisitedStepIndex: 0,
    };
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  }

  function reset() {
    wizardState.value = undefined;
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  }

  return {
    isWizardInitialized,
    targetSubscription,
    sourceSubscription,
    latestVisitedStepIndex,
    unsavedChangesState,
    planList,
    isUpgradeConfirmationShown,
    paymentCheckoutSession,
    paymentMethodUpdateCheckoutSession,
    loadAvailablePlans,
    confirmUnsavedChangesBeforeLeaving,
    init,
    reset,
  };
});
